import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client";

import { ApolloQueryData, PROMOTION_QUERY } from "src/utils/promotion_query";
import { CloseIcon } from "src/assets/icons/close_icon";
import { HamburgerIcon } from "src/assets/icons/hamburger_icon";
import { UserIcon } from "src/assets/icons/user_icon";
import { LogoutIcon } from "src/assets/icons/logout_icon";
import { LoginIcon } from "src/assets/icons/login_icon";
import { LogOutInput, LogOutPayload } from "src/generated/types";
import { DELETE_COOKIE_REQUEST } from "src/state/ducks/auth";
import { RootState } from "src/state/reducers";
import { Routes } from "src/utils/routes";

import "./navbar.scss";

/*
 * GraphQL
 */

export const LOGOUT_MUTATION = gql`
    mutation Logout($input: LogOutInput!) {
        logOut(input: $input) {
            viewer {
                user {
                    id
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloData {
    logOut: LogOutPayload;
}

interface ApolloMutationVars {
    input: LogOutInput;
}

/*
 * Main
 */

const requiredSteps = ["accountInformation", "needsConstituentGroups"];

export const Navbar = (): React.ReactElement => {
    const dispatch = useDispatch();
    const { data, error, loading } = useQuery<ApolloQueryData, Empty>(PROMOTION_QUERY);
    const [logout, { client }] = useMutation<ApolloData, ApolloMutationVars>(LOGOUT_MUTATION);
    const { isAdmin, isLoggedIn } = useSelector((state: RootState) => state.auth);
    const [isNavOpen, setNavOpen] = useState<boolean>(false);

    const surveyDisabled =
        !loading && !error && requiredSteps.includes(data?.viewer.user.school?.currentReport?.status || "");

    return (
        <div className="acsi-global-nav" id="acsi-global-nav">
            <div className="global-inner">
                <div className="global-primary">
                    <div className="ginner">
                        <a
                            aria-label="ACSI.org"
                            className="global-logo"
                            href="https://www.acsi.org/"
                            rel="noopener noreferrer"
                            target="_blank"
                        />
                        <div className="gnav-wraps">
                            <nav className="global-nav">
                                <ul className="gmenu">
                                    <li>
                                        <a href="https://www.acsi.org/" rel="noopener noreferrer" target="_blank">
                                            ACSI.org
                                        </a>
                                    </li>
                                    <li>
                                        <a href="https://blog.acsi.org" rel="noopener noreferrer" target="_blank">
                                            Blog
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="http://acsi.learningbuilder.com/"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            Learning
                                        </a>
                                    </li>
                                    <li>
                                        <a href="https://community.acsi.org" rel="noopener noreferrer" target="_blank">
                                            Community
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                            <div className="global-account-nav">
                                <ul className="gmenu">
                                    <li>
                                        <a href="http://your.acsi.org/" rel="noopener noreferrer" target="_blank">
                                            <UserIcon />
                                            My Account
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <a
                            aria-label="Open Navigation"
                            className="global-m-handle"
                            id="global-mobile-handle"
                            onClick={(): void => setNavOpen(!isNavOpen)}
                        >
                            <HamburgerIcon />
                            <CloseIcon />
                        </a>
                    </div>
                </div>
                <div
                    className={`global-secondary global-secondary-blog nav-${isNavOpen ? "open" : "closed"}`}
                    id="acsi-global-secondary-nav"
                >
                    <div className="ginner">
                        <div className="gnav-wraps">
                            <nav className="global-nav">
                                <ul className="gmenu">
                                    {isAdmin ? null : (
                                        <>
                                            <li>
                                                <NavLink
                                                    exact
                                                    onClick={(): void => setNavOpen(false)}
                                                    to={Routes.dashboard}
                                                >
                                                    FSCI and FFI Dashboard
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink
                                                    exact
                                                    onClick={(): void => setNavOpen(false)}
                                                    to={Routes.about}
                                                >
                                                    About
                                                </NavLink>
                                            </li>
                                            <li>
                                                {surveyDisabled ? (
                                                    <a className="nav-item--disabled">Survey Promotion</a>
                                                ) : (
                                                    <NavLink
                                                        exact
                                                        onClick={(): void => setNavOpen(false)}
                                                        to={Routes.promotion}
                                                    >
                                                        Survey Promotion
                                                    </NavLink>
                                                )}
                                            </li>
                                            <li>
                                                <NavLink
                                                    exact
                                                    onClick={(): void => setNavOpen(false)}
                                                    to={Routes.report.home}
                                                >
                                                    Reports
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink
                                                    exact
                                                    onClick={(): void => setNavOpen(false)}
                                                    to={Routes.account}
                                                >
                                                    My FSCI Account
                                                </NavLink>
                                            </li>
                                        </>
                                    )}
                                    {isLoggedIn ? (
                                        <li>
                                            <a
                                                onClick={(): void => {
                                                    setNavOpen(false);
                                                    logout({
                                                        variables: {
                                                            input: { clientMutationId: "" },
                                                        },
                                                    }).then(async () => {
                                                        dispatch({
                                                            type: DELETE_COOKIE_REQUEST,
                                                        });
                                                        await client?.clearStore();
                                                        window.location.href = Routes.auth;
                                                    });
                                                }}
                                            >
                                                <LogoutIcon />
                                                Logout
                                            </a>
                                        </li>
                                    ) : (
                                        <li>
                                            <NavLink exact onClick={(): void => setNavOpen(false)} to={Routes.auth}>
                                                <LoginIcon />
                                                Login
                                            </NavLink>
                                        </li>
                                    )}
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
